import React from 'react';
import { find, get, isEmpty } from 'lodash';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// local dependencies
import { authFlowCallerIdentificationDoctor as AuthFlowCallerIdentificationDoctorView } from 'ui/views/widgets/authFlowCallerIdentificationDoctor';
import { AuthFlowCallerIdentificationDoctorService } from 'services/authFlowCallerIdentificationDoctor';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION, PAGE_LEVEL_PRELOADER_TEMPLATES } from 'constants/common';
import { REDUCER } from 'constants/entity-names';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { updateCallState, initCallState } from 'store/actions/callState';
import { isInbound, getCallerTypeFromName } from 'resolvers/callStateEntities';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { AuthFlowCallerIdentificationDoctor } from 'ui/containers/AuthFlowCallerIdentificationDoctor';
 *
 * // render
 * <AuthFlowCallerIdentificationDoctor propName='propValue'/>
 */
export class _AuthFlowCallerIdentificationDoctor extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind methods to instance
        this.__bind();
        
        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
			mode: 'editable', // could be `readonly` or `editable`
			isSearchDoctorVisible: false, //search visibility
			doctorSearchType: 'NPI',
			npiNumber: null,
            isCriteriaVisible: false,
            isMemberVerified: false,
            actionsForNext: null,
            dataForMatchCriteria: null,
            doctorInfo: {},
        };

        // create service instance
        this.authFlowCallerIdentificationDoctorService = new AuthFlowCallerIdentificationDoctorService();
        
        // start data fetch
        this.fetchComponentData();
    }

    // bind methods and events to instance
	__bind() {

		this.handleToggleEditMode = this.handleToggleEditMode.bind( this );
        this.handleFormSubmit = this.handleFormSubmit.bind( this );
		this.handleDoctorSearchTypeChange = this.handleDoctorSearchTypeChange.bind( this );
		this.onSelectDoctorSearch = this.onSelectDoctorSearch.bind( this );
		this.handleDoctorSearchQueryChange = this.handleDoctorSearchQueryChange.bind( this );
        this.handleActionClick = this.handleActionClick.bind( this );
        this.handleMarkVerifiedAndShowNext = this.handleMarkVerifiedAndShowNext.bind( this );
        this.saveCriteriaSelection = this.saveCriteriaSelection.bind( this );
	}

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'AuthFlowCallerIdentificationDoctor.fetchComponentData()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationDoctorService.cancel();

        // fetch component data
        const { status, data } = await this.authFlowCallerIdentificationDoctorService.fetchComponentData( {
            url: this.props.widget.url,
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }

    /**
     * @desc Request backend to register `member` as the caller for the ongoing inbound call.
     */
    async registerInboundCaller( { actionName, url, payload, callerType } ) {
        log.debug( 'AuthFlowCallerIdentificationDoctor.registerInboundCaller()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationDoctorService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // fetch component data
        const { status } = await this.authFlowCallerIdentificationDoctorService.registerInboundCaller( {
            url: url,
            data: payload,
            callerType: callerType
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {

                // update call data in the store
                this.props.updateCallState( {
                    callerId: payload.id,
                    callerType: callerType,
                    callerInfo: {
                        id: null,
                        refId: null,
                        type: callerType,
                        firstName: null,
                        lastName: null,
                    },
                } );
                
                // move to the next step
                this.props.onNextActionClick( { name: actionName } );
            } );
        } else {
            errorOverlay.open(); // show error
        }

    }
    
    /**
     * @desc Mark member as verified (from criteria match widget).
     */
    handleMarkVerifiedAndShowNext( { isVerified = false } ) {
        log.debug( 'AuthFlowCallerIdentificationDoctor.handleMarkVerifiedAndShowNext()' );

        this.setState( {
            isMemberVerified: isVerified,
        } );
    }

    /**
	 * @desc Toggle between read and edit mode.
	 */
	handleToggleEditMode( editable = true ) {
		log.debug( 'AuthFlowCallerIdentificationDoctor.handleToggleEditMode() ' );

		this.setState( {
			mode: editable ? 'editable' : 'readonly',
		} );
	}

    /**
	 * @desc Handle form submit.
	 */
	async handleFormSubmit( { title, firstName, lastName } = {} ) {
		log.debug( 'AuthFlowCallerIdentificationDoctor.handleFormSubmit()' );

		// get save item action
		const saveAction = find( this.state.componentData.sections.callerDetails.actions, { name: 'save' } );

		// cancel ongoing request
		this.authFlowCallerIdentificationDoctorService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const { status, data } = await this.authFlowCallerIdentificationDoctorService.addUpdateItem( {
			url: saveAction.url,
			data: {
				operation: isEmpty( this.state.doctorInfo ) ? CRUD_OPERATION.CREATE : CRUD_OPERATION.UPDATE,
				title,
                firstName,
                lastName
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

			/*----------*/

			this.setState( {
				isFetching: false,
				isSearchDoctorVisible: true,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				mode: 'readonly',
				doctorInfo: data
			} );
		} else {
			errorOverlay.open(); // show error
		}
    }

    /**
	 * @desc When doctor type changes, update value in the state
	 */
    handleDoctorSearchTypeChange( selectedType ) {
		log.debug( 'AuthFlowCallerIdentificationDoctor.handleDoctorSearchTypeChange()' );

        this.setState( {
            doctorSearchType: selectedType,
        } );
    }

    /**
	 * @desc fetch match criteria for the doctor
	 */
	async onSelectDoctorSearch( searchVal ) {
		log.debug( 'AuthFlowCallerIdentificationDoctor.onSelectDoctorSearch()' );

		// cancel ongoing request
		this.authFlowCallerIdentificationDoctorService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const { status, data } = await this.authFlowCallerIdentificationDoctorService.fetchMatchCriteria( {
			url: this.state.componentData.sections.callerVerification.url,
			data: {
                id: searchVal,
                idType: this.state.doctorSearchType,
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				npiNumber: searchVal,
                isCriteriaVisible: true,
                dataForMatchCriteria: data,
                isMemberVerified: false,
			} );
		} else {
			errorOverlay.open(); // show error
		}
    }

    /**
     * @desc When doctor search query change, hide criteria view
     */
    handleDoctorSearchQueryChange() {
		log.debug( 'AuthFlowCallerIdentificationDoctor.handleDoctorSearchQueryChange()' );

        if( this.state.isCriteriaVisible ) {
            this.setState( {
                isCriteriaVisible: false,
                isMemberVerified: false
            } );
        }
    }

    /**
     * @desc Update criteria selection on the backend.
     */
    async saveCriteriaSelection( { saveAction, fieldStatus } ) {

        log.debug( 'AuthFlowCallerIdentificationDoctor.saveCriteriaSelection()' );

        if( isEmpty( saveAction ) ) {
            return;
        }

        /*----------*/

        // cancel ongoing request
        this.authFlowCallerIdentificationDoctorService.cancel();

        // show page level preloader
        pageLevelPreloader.show( PAGE_LEVEL_PRELOADER_TEMPLATES.NANOBAR );

        // get status and service data
        const { status } = await this.authFlowCallerIdentificationDoctorService.saveCriteriaSelection( {
            url: saveAction.url,
            data: {
                id: this.state.npiNumber,
                idType: this.state.doctorSearchType,
                ...fieldStatus
            },
        } );

        // hide page level preloader
        pageLevelPreloader.hide( PAGE_LEVEL_PRELOADER_TEMPLATES.NANOBAR );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.CANCELLED === status ) {
            return null; // ignore on request cancellations
         } else if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }

    /* ************************************************************* */

	/**
     * @desc Action handler for next button click.
     */
    handleActionClick( { item = {} } = {} ) {
        log.debug( 'AuthFlowCallerIdentificationDoctor.handleActionClick()'  );

        // get member detail
        const firstName = get( item, 'firstName', '' );
        const lastName = get( item, 'lastName', '' );

        // extract actions
        const actions = get( this.state.dataForMatchCriteria, 'actions' );

        // getting caller type value
        const callerType = getCallerTypeFromName( this.props.widget.value.callerType );

        /*---------*/

        // if its inbound call, register an inbound caller
        if( isInbound() ) {
            const action = find( actions, { name: 'nextInbound'} );

            return this.registerInboundCaller( {
                actionName: action.name,
                url: action.url,
                payload: {
                    id: this.state.npiNumber,
                    firstName,
                    lastName,
                },
                callerType: callerType
            } );
        }
    }

    /*********************************************/

    /**
     * @desc When component unMounts, cleanup resources.
    */
    componentWillUnmount() {
        this.authFlowCallerIdentificationDoctorService.cancel(); // cancel ongoing requests
    }

    /*********************************************/

    /**
     * @desc Render a React view component
     */
    render() {

        // getting caller type value
        const callerType = getCallerTypeFromName( this.props.widget.value.callerType );

        return (
            <AuthFlowCallerIdentificationDoctorView
                className={ this.props.className }
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                hasCallStarted={ ! isEmpty( this.props.callState ) }
                componentData={ get( this.state, 'componentData', {} ) }
                onFormSubmit={ this.handleFormSubmit }
                onToggleEditMode={ this.handleToggleEditMode }
                mode={ this.state.mode }
                doctorSearchType={ this.state.doctorSearchType }
                onDoctorSearchTypeChange={ this.handleDoctorSearchTypeChange }
                onSelectDoctorSearch={ this.onSelectDoctorSearch }
                onDoctorSearchQueryChange={ this.handleDoctorSearchQueryChange }
                isSearchDoctorVisible={ this.state.isSearchDoctorVisible }
                isCriteriaVisible={ this.state.isCriteriaVisible }
                npiNumber={ this.state.npiNumber }
                handleActionClick={ this.handleActionClick }
                handleMarkVerifiedAndShowNext={ this.handleMarkVerifiedAndShowNext }
                isMemberVerified={ this.state.isMemberVerified }
                actionsForNext={ this.state.actionsForNext }
                dataForMatchCriteria={ this.state.dataForMatchCriteria }
                saveCriteriaSelection={ this.saveCriteriaSelection }
                callerType={ callerType }
                doctorInfo={ this.state.doctorInfo }
            />
        );
    }
}

// set display name
_AuthFlowCallerIdentificationDoctor.displayName = 'AuthFlowCallerIdentificationDoctor';

// set default props
_AuthFlowCallerIdentificationDoctor.defaultProps = {};

// prop types
_AuthFlowCallerIdentificationDoctor.propTypes = {};

/************************************************************ */

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        initCallState: bindActionCreators( initCallState, dispatch ),
        updateCallState: bindActionCreators( updateCallState, dispatch ),
    };
};

// return component with acccess to Redux store
export const AuthFlowCallerIdentificationDoctor = withRouter( connect( mapStateToProps, mapDispatchToProps )( _AuthFlowCallerIdentificationDoctor ) );

